import { type FC } from "react";
import { useTranslation } from "@privacy/translations";
import { useForm } from "react-hook-form";
import Tooltip from "@mui/material/Tooltip";
import { useCreatePersonalDataController } from "../../../api/personalDataControllers";
import { useGetMyTenantsHierarchy } from "../../../api/users";
import type { TenantHierarchy } from "../../../api/users.types";
import * as FormDialog from "../../../shared/FormDialog";
import { TenantSelect } from "../../../shared/TenantSelect";
import type { CreatePersonalDataControllerPayload } from "../../../api/personalDataControllers.types";

type CreatePersonalDataControllerDialogProps = {
  existingPdcTenantIds: string[];
  onClose: () => void;
  onCreated: (id: string) => void;
};

export const CreatePersonalDataControllerDialog: FC<CreatePersonalDataControllerDialogProps> = ({
  existingPdcTenantIds,
  onClose,
  onCreated,
}) => {
  const { t } = useTranslation();
  const { mutate: createPersonalDataController, isError, isPending: isSubmitting } = useCreatePersonalDataController();
  const { data: tenantPermissionsHierarchy } = useGetMyTenantsHierarchy();
  const { handleSubmit, reset, control, setValue, setFocus } = useForm<CreatePersonalDataControllerPayload>({
    defaultValues: { tenantId: "" },
  });

  FormDialog.useAutoFocusField(setFocus, "tenantId");

  const handleNewPersonalDataController = ({ tenantId }: CreatePersonalDataControllerPayload) =>
    createPersonalDataController(
      { tenantId },
      {
        onSuccess: async ({ id }) => {
          onClose();
          reset();
          onCreated(id);
        },
      }
    );

  const handleCancel = () => {
    onClose();
    reset();
  };

  const isTenantAlreadyUsed = (tenant: TenantHierarchy) => existingPdcTenantIds.includes(tenant.id);

  return (
    <FormDialog.Dialog maxWidth="34rem" isOpen={true}>
      <FormDialog.Title>{t("CreatePersonalDataController.Dialog.CreateTitle")}</FormDialog.Title>

      <FormDialog.Form onSubmit={handleSubmit(handleNewPersonalDataController)}>
        <FormDialog.Content>
          <FormDialog.FormError isError={isError} />
          <FormDialog.Fields>
            <FormDialog.ControlledField
              control={control}
              name="tenantId"
              id="tenantId"
              rules={{ required: t("CreatePersonalDataController.Dialog.GenericError") }}
              label={t("CreatePersonalDataController.Dialog.TenantsLabel")}
            >
              {({ field: { value, name, ref, onBlur }, fieldState, id }) => (
                <TenantSelect
                  id={id}
                  error={Boolean(fieldState.error)}
                  selectedId={value}
                  onSelect={selected => setValue(name, selected.id, { shouldValidate: Boolean(selected) })}
                  tenantHierarchy={tenantPermissionsHierarchy}
                  inputRef={ref}
                  onBlur={onBlur}
                  renderOption={(optionElement, tenant) => (
                    <Tooltip
                      key={tenant.id}
                      title={
                        isTenantAlreadyUsed(tenant) && t("CreatePersonalDataController.Dialog.TenantAlreadyUsedTooltip")
                      }
                      followCursor
                    >
                      {optionElement}
                    </Tooltip>
                  )}
                  forceDisabledTenant={isTenantAlreadyUsed}
                />
              )}
            </FormDialog.ControlledField>
          </FormDialog.Fields>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={handleCancel} />
          <FormDialog.SubmitButton isSubmitting={isSubmitting}>
            {t("CreatePersonalDataController.Dialog.CreateSubmitButton")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};
